import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { filterBrandConfigRows } from "../helperOfBrandConfig/brandConfigHelpers";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";

const size = {
  width: 120,
  height: 120,
};

// One fixed color per legend group (keys must match the labels built below)
const STATUS_COLORS: Record<string, string> = {
  Ready: "#4CAF50", // green
  Running: "#8E24AA", // purple
  Pending: "#F06292", // pink
  "Stuck at Validations": "#FF9800", // orange
  "Stuck at Fetcher": "#ed0a5a", // crimson
  "Stuck at Advisor": "#3384E8", // light blue
  "Stuck at Unifier": "#3F51B5", // indigo / royal blue
  Properties: "#FDD835", // yellow
};

const FALLBACK_COLOR = "#9E9E9E";

// Styled Center Text
const CenterNumber = styled("text")(() => ({
  fill: "#000",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontWeight: 700,
}));

// Center Label Component – just the total now
function PieCenterLabel({ total }: { total: number }) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  const digits = String(total).length;
  const fontSize =
    digits <= 2 ? 23 : digits === 3 ? 18 : digits === 4 ? 15 : 12;

  return (
    <CenterNumber x={centerX} y={centerY} style={{ fontSize }}>
      {total}
    </CenterNumber>
  );
}
// Custom Legend
function CustomLegend({ data }: { data: any[] }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(4, max-content)"
      gap="10px 24px"
    >
      {data.map((entry) => (
        <Box key={entry.label} display="flex" alignItems="center" gap="6px">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "3px",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: "13px", color: "#00000099" }}>
            {entry.label}
          </Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#000" }}>
            {entry.count} ({entry.percent})
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// Main Component
export default function BrandConfigurationReportChart() {
  const {
    brandConfigurationReportList: modifiedbrandConfiguration,
    selectedBrandName,
    selectedIntegrationStatus,
    selectedStuckAt,
    selectedReason,
    selectedAffiliatedSystem,
    selectedWebsite,
    selectedLastRun,
  } = useSelector((state: any) => state.brandConfigurationReport);

  const allRows = Array.isArray(modifiedbrandConfiguration)
    ? modifiedbrandConfiguration
    : [];

  // Filter rows
  const rows = filterBrandConfigRows(allRows, {
    selectedBrandName,
    selectedIntegrationStatus,
    selectedStuckAt,
    selectedReason,
    selectedAffiliatedSystem,
    selectedWebsite,
    selectedLastRun,
  });

  // Bucket every row into exactly one group
  const counts: Record<string, number> = {};

  rows.forEach((item: any) => {
    const status = (item?.integration_status ?? "").trim();
    let label: string | null = null;

    if (status === "Ready") {
      label = "Ready";
    } else if (status === "Running") {
      label = "Running";
    } else if (status === "Pending") {
      label = "Pending";
    } else if (status === "Not Ready") {
      const step = (item?.additional_information?.step ?? "").trim();
      if (!step) return; // Not Ready with no step -> skip
      // Normalize casing so "validations" -> "Validations"
      const normalizedStep =
        step?.charAt(0).toUpperCase() + step?.slice(1).toLowerCase();
      label =
        normalizedStep === "Properties"
          ? "Properties"
          : `Stuck at ${normalizedStep}`;
    } else {
      return; // any other / empty status -> ignore
    }

    counts[label] = (counts[label] || 0) + 1;
  });

  // Center number = total rows in the current filtered selection
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);

  // Build chart data, sorted by count desc (matches the legend order)
  const chartData = Object.entries(counts)
    .map(([label, count]) => ({
      label,
      count,
      value: count,
      percent: total > 0 ? `${((count / total) * 100).toFixed(1)}%` : "0%",
      color: STATUS_COLORS[label] || FALLBACK_COLOR,
    }))
    .sort((a, b) => b.count - a.count);

  if (chartData.length === 0 || total === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F7F7F8",
        borderRadius: "10px",
        px: 2.5,
        py: 2,
        mb: 3,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <PublishedWithChangesRoundedIcon sx={{ fontSize: 20, color: "#8E24AA" }} />
        <Typography
          sx={{ fontSize: "16px", fontWeight: 500, color: "#000000DE" }}
        >
          Quick View: Integration Status
        </Typography>
      </Stack>

      {/* Chart + Legend */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexShrink: 0, width: size.width, height: size.height }}>
          <PieChart
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            series={[
              {
                data: chartData.map((d: any) => ({
                  label: d.label,
                  value: d.value,
                  color: d.color,
                })),
                 innerRadius: 40,
              outerRadius: 50,
              } as any,
            ]}
            {...size}
            slotProps={{ legend: { hidden: true } }}
          >
            <PieCenterLabel total={total} />
          </PieChart>
        </Box>
        <CustomLegend data={chartData} />
      </Stack>
    </Box>
  );
}
